import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { JournalPreloader, ToastTypes } from 'atoms';
import { HomeworkGradesType } from 'types';
import {
  useGradeHomework,
  useHomeworkRoster,
  useLessonHomeworkAssignment,
  useToaster,
  useUpsertHomeworkAssignment,
} from 'core';
import { HomeworkReviewDrawer } from 'organisms/HomeworkReviewDrawer';
import { getHomeworkErrorText } from 'organisms/HomeworkSubmit';

import { HomeworkCheckComponent } from './HomeworkCheck';
import type { IHomeworkAssignmentFormValues } from './HomeworkAssignmentForm';

interface IHomeworkCheckContainer {
  lessonId: string;
}

/**
 * The single homework grading system (spec 0012): the roster comes from
 * homeworkRoster and grading goes through gradeHomework in the review drawer —
 * the legacy setStudentsMarks path stays only for coursework/graduation.
 *
 * The selected drawer row is DERIVED from the roster by studentId, so the
 * roster refetch after grading / AI-rerun updates the open drawer in place.
 */
export const HomeworkCheck = memo(({ lessonId }: IHomeworkCheckContainer) => {
  const { t } = useTranslation();
  const { addToast } = useToaster();

  const {
    assignment,
    textbookFiles,
    loading: assignmentLoading,
    error: assignmentError,
    refetch: refetchAssignment,
  } = useLessonHomeworkAssignment(lessonId);
  // NOT gated on the assignment: the roster resolver never required one, and
  // the old grid graded lessons whose homework lives outside the platform —
  // gating it here silently removed grading from every running group.
  const {
    roster,
    aiEnabled,
    loading: rosterLoading,
    error: rosterError,
    refetch: refetchRoster,
  } = useHomeworkRoster(lessonId);
  const { upsertAssignment, loading: saveLoading } = useUpsertHomeworkAssignment();
  const { gradeHomework } = useGradeHomework();

  const [editing, setEditing] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);

  const selectedRow = useMemo(
    () => roster.find((row) => row.studentId === selectedStudentId) ?? null,
    [roster, selectedStudentId],
  );

  const onSaveAssignment = useCallback(
    async (values: IHomeworkAssignmentFormValues): Promise<boolean> => {
      try {
        const result = await upsertAssignment(lessonId, values);
        if (result.success) {
          setEditing(false);
          addToast('homework_assignment', {
            type: ToastTypes.success,
            text: `${t('homework.assignment.form.saveSuccess')}`,
          });
          return true;
        }
        addToast('homework_assignment', {
          type: ToastTypes.danger,
          text: getHomeworkErrorText(t, result.errorCode),
        });
        return false;
      } catch {
        addToast('homework_assignment', {
          type: ToastTypes.danger,
          text: `${t('homework.assignment.form.saveError')}`,
        });
        return false;
      }
    },
    [upsertAssignment, lessonId, addToast, t],
  );

  const ungradedSubmitted = useMemo(
    () => roster.filter((row) => row.submission && !row.hwMark),
    [roster],
  );

  /**
   * Bulk «submitted but ungraded → Выполнено» — the old grid's one-click
   * «все сделали», rebuilt conservatively: only rows that HAVE a submission
   * and no mark, one existing mutation per row, never touching non-submitters.
   * No `attempt` is passed: this runs straight off the roster just rendered.
   */
  const onBulkGradeDone = useCallback(async () => {
    const rows = ungradedSubmitted;
    setBulkProgress({ done: 0, total: rows.length });
    let failed = 0;
    for (let i = 0; i < rows.length; i += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const result = await gradeHomework(
          lessonId,
          rows[i].studentId,
          HomeworkGradesType.DONE,
          '',
        );
        if (!result.success) {
          failed += 1;
        }
      } catch {
        failed += 1;
      }
      setBulkProgress({ done: i + 1, total: rows.length });
    }
    setBulkProgress(null);
    refetchRoster();
    addToast('homework_bulk', {
      type: failed ? ToastTypes.danger : ToastTypes.success,
      text: failed
        ? `${t('homework.roster.bulkPartial', { n: failed })}`
        : `${t('homework.roster.bulkSuccess', { n: rows.length })}`,
    });
  }, [ungradedSubmitted, gradeHomework, lessonId, refetchRoster, addToast, t]);

  if (assignmentLoading) {
    return <JournalPreloader />;
  }

  return (
    <>
      <HomeworkCheckComponent
        assignment={assignment}
        textbookFiles={textbookFiles}
        assignmentError={assignmentError}
        onRetryAssignment={refetchAssignment}
        editing={editing}
        saveLoading={saveLoading}
        roster={roster}
        rosterLoading={rosterLoading}
        rosterError={rosterError}
        bulkCount={ungradedSubmitted.length}
        bulkProgress={bulkProgress}
        onBulkGradeDone={onBulkGradeDone}
        onStartEdit={() => setEditing(true)}
        onCancelEdit={() => setEditing(false)}
        onSaveAssignment={onSaveAssignment}
        onOpenStudent={setSelectedStudentId}
      />
      <HomeworkReviewDrawer
        open={Boolean(selectedRow)}
        lessonId={lessonId}
        row={selectedRow}
        aiEnabled={aiEnabled}
        onClose={() => setSelectedStudentId(null)}
      />
    </>
  );
});
